import React, { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { asFetchableBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { DefaultView } from '../../types/BlockWithViews';
import { ContentParagraph, ContentParagraphModifier } from '../../_lib/components/ContentParagraph';
import { Namespace } from '../../translation/namespace';
import { DefinitionList, createDefinitionTerm } from '../../_lib/components/DefinitionList';
import { Heading } from '../../_lib/components/Heading/Heading';

export type DeviatingValidityPeriodsCategory = {
  id: string;
  pageTypeId: string;
  title: string;
  maxValidityPeriodDays: number;
};

type DeviatingValidityPeriodsBlockProps = {
  views: DefaultView<DeviatingValidityPeriodsProps>;
};

type DeviatingValidityPeriodsProps = {
  categories: DeviatingValidityPeriodsCategory[];
};

/**
 * Information about deviating validity Periods.
 * used in Portal settings, currently only containing a default view which will be shown in edit or default view.
 */
function DeviatingValidityPeriods({ views }: DeviatingValidityPeriodsBlockProps) {
  const { categories } = views.default.embedded;
  const { t } = useTranslation(Namespace.DeviatingValidityPeriods);

  if (categories?.length === 0) {
    return null;
  }

  const getValidationPeriod = (category: DeviatingValidityPeriodsCategory): string => {
    return category.maxValidityPeriodDays === 0
      ? t('unlimited')
      : t('inDays', { validity: category.maxValidityPeriodDays });
  };

  const mappedCategories = categories
    .map(category => {
      const categoryTitle = createDefinitionTerm(`${t('category')}: `, category.title);
      const categoryValidity = createDefinitionTerm(`${t('maxValidity')}: `, `${getValidationPeriod(category)}`);

      return [categoryTitle, categoryValidity];
    })
    .flat();

  return (
    <div className="deviating-validity-periods">
      <Heading className="deviating-validity-periods__heading" hierarchy={3}>
        {t('headline')}
      </Heading>
      <ContentParagraph modifier={ContentParagraphModifier.MARGIN_BOTTOM}>{t('description')}</ContentParagraph>
      <DefinitionList className="deviating-validity-periods__list" definitionTerms={mappedCategories} />
    </div>
  );
}

export const DeviatingValidityPeriodsBlock = asFetchableBlock(
  DeviatingValidityPeriods,
  BlockType.deviatingvalidityperiods
);
export type DeviatingValidityPeriodsBlock = ComponentProps<typeof DeviatingValidityPeriodsBlock>;
